import { useEffect, useMemo, useState } from 'react';
import useCheckLists from "../../components/SyncManager/CheckListSync";
import useComments from "../../components/SyncManager/CommentsSync";
import usePunchlists from "../../components/SyncManager/PunchlistSync";
import PendingItem from './PendingItem';
import { twMerge } from 'tailwind-merge';
import SectionHeader from '../../components/CoreComponents/Details/SectionHeader';
import { ObjectToSync } from '../../types/types';
import { SyncItemsTypes } from '../../components/SyncManager/SyncItemsTypes';

type QueueItem = {
    id: string,
    status?: 'retry' | 'dead-letter' | 'conflict',
    itemType: string,
    itemId?: string,
    itemLabel?: string,
    attempts: number,
    lastError?: string,
    nextAttemptAt?: number,
    localTimestamp?: string | null,
    serverTimestamp?: string | null,
}

type SyncQueueStatusPayload = {
    retry: QueueItem[],
    deadLetter: QueueItem[],
    conflicts: QueueItem[],
    counts: {
        retry: number,
        deadLetter: number,
        conflicts: number,
    }
}

export default function PendingSyncItems() {
    const { getChecklistItemsToSync, cancelSyncItem: cancelChecklistSyncItem } = useCheckLists();
    const checklists = getChecklistItemsToSync();
    const { getCommentsToSync, cancelSyncItem: cancelCommentSyncItem } = useComments();
    const comments = getCommentsToSync();
    const { getPunchlistsToSync, cancelSyncItem: cancelPunchlistSyncItem } = usePunchlists();

    const [pls, setPls] = useState([]);
    const [queueStatus, setQueueStatus] = useState<SyncQueueStatusPayload>({
        retry: [],
        deadLetter: [],
        conflicts: [],
        counts: { retry: 0, deadLetter: 0, conflicts: 0 }
    });

    const requestQueueStatus = async () => {
        if (!('serviceWorker' in navigator)) return;
        const registration = await navigator.serviceWorker.getRegistration().catch(() => null);
        const active = navigator.serviceWorker.controller ?? registration?.active ?? registration?.waiting ?? registration?.installing;
        active?.postMessage({ type: 'GET_SYNC_QUEUE_STATUS' });
    };

    const sendQueueAction = async (type: string, id: string) => {
        if (!('serviceWorker' in navigator)) return;
        const registration = await navigator.serviceWorker.getRegistration().catch(() => null);
        const active = navigator.serviceWorker.controller ?? registration?.active ?? registration?.waiting ?? registration?.installing;
        active?.postMessage({ type, id });
    };

    useEffect(() => {
        (async () => {
            const punchlistsPromises = getPunchlistsToSync();
            const punchlists = [];
            for (let k = 0; k < punchlistsPromises.length; k++) {
                const pl = await punchlistsPromises[k];
                punchlists.push(pl);
            }
            setPls(punchlists);
        })();
    }, []);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        const messageListener = (event) => {
            if (event.data?.type === 'SYNC_QUEUE_STATUS' && event.data?.data) {
                setQueueStatus(event.data.data as SyncQueueStatusPayload);
            }
        };

        navigator.serviceWorker.addEventListener('message', messageListener);
        requestQueueStatus();

        const intervalId = setInterval(() => {
            requestQueueStatus();
        }, 5000);

        return () => {
            navigator.serviceWorker.removeEventListener('message', messageListener);
            clearInterval(intervalId);
        };
    }, []);


    // Refetch punchlists whenever checklists or comments change to keep pending items in sync
    useEffect(() => {
        (async () => {
            const punchlistsPromises = getPunchlistsToSync();
            const punchlists = [];
            for (let k = 0; k < punchlistsPromises.length; k++) {
                const pl = await punchlistsPromises[k];
                punchlists.push(pl);
            }
            setPls(punchlists);
        })();
    }, [checklists.length, comments.length]);

    const totalPendingItems = checklists.length + comments.length + pls.length;
    const headerTitle = totalPendingItems === 0 ? "No Pending Sync Items" : "Pending Sync Items";

    const queueEntriesMap = useMemo(() => {
        const entries = [...queueStatus.retry, ...queueStatus.deadLetter, ...queueStatus.conflicts];
        return entries.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {} as Record<string, QueueItem>);
    }, [queueStatus]);

    const getQueueIdForItem = (item: ObjectToSync): string => {
        if (item.syncMeta?.queueId) return item.syncMeta.queueId;
        const itemId = item.syncMeta?.itemId || item.payload?.ctUuid || item.payload?.commentUuid || item.payload?.punchListUuid || 'unknown';
        return `${item.type}:${itemId}`;
    };

    const resolveItemId = (item: ObjectToSync): string => {
        return item.syncMeta?.itemId || item.payload?.ctUuid || item.payload?.commentUuid || item.payload?.punchListUuid || '';
    };

    const retryPendingItem = async (item: ObjectToSync) => {
        if (!('serviceWorker' in navigator)) return;
        const registration = await navigator.serviceWorker.getRegistration().catch(() => null);
        const active = navigator.serviceWorker.controller ?? registration?.active ?? registration?.waiting ?? registration?.installing;
        if (!active) return;
        active.postMessage({ isOnline: true, syncRequest: item });
        active.postMessage({ type: 'PROCESS_SYNC_QUEUE' });
        requestQueueStatus();
    };

    const cancelPendingItem = async (item: ObjectToSync) => {
        const itemId = resolveItemId(item);
        if (!itemId) return;
        const queueId = getQueueIdForItem(item);
        const normalizedItemId = itemId as any;

        switch (item.type) {
            case SyncItemsTypes.CheckListItem:
                cancelChecklistSyncItem(normalizedItemId);
                break;
            case SyncItemsTypes.Comment:
                cancelCommentSyncItem(normalizedItemId);
                break;
            case SyncItemsTypes.PunchlistItem:
                cancelPunchlistSyncItem(normalizedItemId);
                break;
            default:
                break;
        }

        sendQueueAction('DISCARD_SYNC_ITEM', queueId);

        const punchlistsPromises = getPunchlistsToSync();
        const punchlists = [];
        for (let k = 0; k < punchlistsPromises.length; k++) {
            const pl = await punchlistsPromises[k];
            punchlists.push(pl);
        }
        setPls(punchlists);
        requestQueueStatus();
    };

    const pendingItems = useMemo(() => {
        return [...checklists, ...comments, ...pls] as ObjectToSync[];
    }, [checklists, comments, pls]);

    return (
        <>
            <div className="flex gap-3 items-center mb-2">
                <div className={twMerge("flex gap-3 items-center")}>
                    <SectionHeader title={headerTitle} />
                </div>
            </div>

            {queueStatus.counts.conflicts > 0 && (
                <div className="m-2 p-3 border rounded bg-yellow-50 border-yellow-300">
                    <p className="font-semibold text-yellow-800">Conflicts detected ({queueStatus.counts.conflicts})</p>
                    <p className="text-sm text-yellow-700">These items were modified on the server while you were offline.</p>
                    <div className="mt-2 space-y-2">
                        {queueStatus.conflicts.map((item) => (
                            <div key={`conflict-${item.id}`} className="p-2 border rounded bg-white">
                                <p className="text-sm font-medium">{item.itemType} {item.itemLabel ? `- ${item.itemLabel}` : ''}</p>
                                <p className="text-xs text-gray-600">Server timestamp: {item.serverTimestamp || 'unknown'}</p>
                                <p className="text-xs text-gray-600">Local timestamp: {item.localTimestamp || 'unknown'}</p>
                                <div className="mt-2 flex gap-2">
                                    <button
                                        className="px-2 py-1 text-xs rounded bg-orange-600 text-white"
                                        onClick={() => sendQueueAction('FORCE_SYNC_ITEM', item.id)}
                                    >
                                        Use My Offline Changes
                                    </button>
                                    <button
                                        className="px-2 py-1 text-xs rounded bg-gray-500 text-white"
                                        onClick={() => sendQueueAction('DISCARD_SYNC_ITEM', item.id)}
                                    >
                                        Discard Offline Change
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {queueStatus.counts.retry > 0 && (
                <div className="m-2 p-3 border rounded bg-blue-50 border-blue-300">
                    <p className="font-semibold text-blue-800">Retry queue ({queueStatus.counts.retry})</p>
                    <p className="text-sm text-blue-700">Failed syncs are being retried with exponential backoff.</p>
                    <div className="mt-2 space-y-2">
                        {queueStatus.retry.map((item) => (
                            <div key={`retry-${item.id}`} className="p-2 border rounded bg-white">
                                <p className="text-sm font-medium">{item.itemType} {item.itemLabel ? `- ${item.itemLabel}` : ''}</p>
                                <p className="text-xs text-gray-600">Attempts: {item.attempts}</p>
                                {item.lastError && <p className="text-xs text-red-700">{item.lastError}</p>}
                                <div className="mt-2 flex gap-2">
                                    <button
                                        className="px-2 py-1 text-xs rounded bg-blue-600 text-white"
                                        onClick={() => sendQueueAction('RETRY_SYNC_ITEM', item.id)}
                                    >
                                        Retry Now
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {queueStatus.counts.deadLetter > 0 && (
                <div className="m-2 p-3 border rounded bg-red-50 border-red-300">
                    <p className="font-semibold text-red-800">Persistent failures ({queueStatus.counts.deadLetter})</p>
                    <p className="text-sm text-red-700">These items exceeded retry attempts and need manual action.</p>
                    <div className="mt-2 space-y-2">
                        {queueStatus.deadLetter.map((item) => (
                            <div key={`dead-${item.id}`} className="p-2 border rounded bg-white">
                                <p className="text-sm font-medium">{item.itemType} {item.itemLabel ? `- ${item.itemLabel}` : ''}</p>
                                <p className="text-xs text-gray-600">Attempts: {item.attempts}</p>
                                {item.lastError && <p className="text-xs text-red-700">{item.lastError}</p>}
                                <div className="mt-2 flex gap-2">
                                    <button
                                        className="px-2 py-1 text-xs rounded bg-red-600 text-white"
                                        onClick={() => sendQueueAction('RETRY_SYNC_ITEM', item.id)}
                                    >
                                        Requeue + Retry
                                    </button>
                                    <button
                                        className="px-2 py-1 text-xs rounded bg-gray-500 text-white"
                                        onClick={() => sendQueueAction('DISCARD_SYNC_ITEM', item.id)}
                                    >
                                        Discard
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {totalPendingItems === 0 ? (
                null
            ) : (
                <>
                    <div className="m-2">
                        {pendingItems.map((item, i) => {
                            const queueId = getQueueIdForItem(item);
                            const queueEntry = queueEntriesMap[queueId];
                            return (
                                <PendingItem
                                    key={`${queueId}-${i}`}
                                    count={totalPendingItems - i}
                                    objectData={item}
                                    errorMessage={queueEntry?.lastError}
                                    queueStatus={queueEntry?.status}
                                    onRetry={retryPendingItem}
                                    onCancel={cancelPendingItem}
                                />
                            );
                        })}
                    </div>
                </>
            )}
        </>
    );
}



